import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { formatImageUrl } from '../../shared/utils/formatImageUrl.js';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';

const EventsPage = () => {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formats, setFormats] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [filterValues, setFilterValues] = useState({ categoryId: null, formatId: null });
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const getData = (count = 5, offset = 0) => {
    apiRequests.events.get(count, offset).then((res) => setData(res));
  };

  const getFilters = () => {
    apiRequests.events.getCategories(100, 0).then((res) => setCategories(res));
    apiRequests.events.getFormats(100, 0).then((res) => setFormats(res));
  };

  useEffect(() => {
    const categoryId = searchParams.get('categoryId');
    const formatId = searchParams.get('formatId');
    
    if (categoryId || formatId) {
      apiRequests.events.getFiltered('', categoryId, formatId).then((res) => setData(res));
    } else {
      getData();
    }
    
    setFilterValues({
      categoryId: categoryId || null,
      formatId: formatId || null,
    });
    getFilters();
  }, []);

  const handleDelete = async (id) => {
    await apiRequests.events.delete(id).then(() => {
      setData((prev) => ({
        totalCount: prev.totalCount,
        items: prev.items.filter((i) => i.id !== id),
      }));
    });
  };

  const handleSearch = () => {
    if (searchValue === '') {
      getData();
    } else {
      apiRequests.events.getFiltered(searchValue, filterValues.categoryId, filterValues.formatId).then((res) => setData(res));
    }
    setSearchParams({});
  };

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setSearchParams((prev) => {
      Object.entries(filterValues).forEach(([key, value]) => {
        if (value) prev.set(key, value);
        else prev.delete(key);
      });
      return prev;
    });
    
    if (filterValues.categoryId || filterValues.formatId) {
      apiRequests.events.getFiltered(searchValue, filterValues.categoryId, filterValues.formatId).then((res) => setData(res));
    } else {
      getData();
    }
  };

  const resetFilters = () => {
    setFilterValues({ categoryId: null, formatId: null });
    setSearchParams({});
    getData();
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories?.items?.map((c) => ({ value: c.id, label: c.title })) || [],
    },
    {
      key: 'formatId',
      type: 'select',
      label: 'Формат',
      value: filterValues.formatId,
      placeholder: 'Все форматы',
      options: formats?.items?.map((f) => ({ value: f.id, label: f.title })) || [],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск мероприятий..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink="/events/create"
      />

      <div className="mb-3">
        {data.items?.length === 0 ? (
          <p className="text-center">По вашему запросу ничего не найдено</p>
        ) : (
          data.items?.map((item) => (
            <EntityCard
              key={item.id}
              image={formatImageUrl(item.imgPath)}
              title={item.title}
              fields={[
                { label: 'Формат', value: item.format?.title },
                { label: 'Категория', value: item.category?.title },
                { label: 'Создано', value: dateToString(item.createdAt) },
              ]}
              editLink={`${location.pathname}/${item.id}`}
              localizationLink={`${location.pathname}/${item.id}/localization`}
              onDelete={() => handleDelete(item.id)}
            />
          ))
        )}
      </div>

      <Pagination
        count={data.totalCount}
        itemsPerPage={5}
        onChange={(page) => getData(5, 5 * page)}
      />
    </>
  );
};

export default EventsPage;
